import React, { useEffect, useState } from 'react'
import { authedAxios } from '../utils'

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material'
import { Add, Info } from '@mui/icons-material'
import useTaskStatusTypes from '../hooks/api/useTaskStatusTypes'
import {
  TASK_TYPES,
  TASK_TYPE_ICON_MAP,
  TASK_TYPE_MODAL_MAP,
} from '../constants'
import TaskModalWrapper from '../customer/task/TaskModalWrapper'
import { updateObjectWithMatchingId } from '../utils/array'
import TaskTypeSelectorModal from './TaskTypeSelectorModal'
import useAlert from '../hooks/useAlert'
import useIsMobile from '../hooks/useIsMobile'
import PastTodayFuture from './PastTodayFuture'
import GenericLoadError from '../common/GenericLoadError'
import TaskItem from './TaskItem'

export const AllTaskModals = ({
  currentOpenModal,
  setCurrentOpenModal,
  currentOpenModalStaging,
  setCurrentOpenModalStaging,
  selectedContact,
  setSelectedContact,
  selectedCustomer,
  setSelectedCustomer,
  currentTask,
  updateTaskInList,
  onClose,
  setCurrentTask,
}) => {
  const { createAlert } = useAlert()
  return (
    <>
      <TaskTypeSelectorModal
        currentOpenModal={currentOpenModal}
        currentOpenModalStaging={currentOpenModalStaging}
        setCurrentOpenModal={setCurrentOpenModal}
        setCurrentOpenModalStaging={setCurrentOpenModalStaging}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
      />

      {Object.keys(TASK_TYPE_MODAL_MAP).map((key) => {
        const Component = TASK_TYPE_MODAL_MAP[key]
        return (
          <TaskModalWrapper
            key={key}
            Component={Component}
            isOpen={currentOpenModal === Number(key)}
            onSave={(task) => {
              setCurrentOpenModal(null)
              updateTaskInList({ ...task, customerName: selectedCustomer.name })
              createAlert({ message: 'Task created successfully' })
              onClose?.()
              setCurrentTask?.(null)
            }}
            onClose={() => {
              setCurrentOpenModal(null)
            }}
            task={currentTask}
            contact={selectedContact}
            customer={selectedCustomer}
          ></TaskModalWrapper>
        )
      })}
    </>
  )
}

const Tasks = () => {
  const [tasks, setTasks] = useState([])
  const [taskTypes, setTaskTypes] = useState([])

  const [currentOpenModal, setCurrentOpenModal] = useState(null)
  const [currentTask, setCurrentTask] = useState(null)
  const [currentOpenModalStaging, setCurrentOpenModalStaging] = useState(
    TASK_TYPES.PHONE_CALL
  )

  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)

  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const { taskStatusTypes, ..._taskStatusTypes } = useTaskStatusTypes()

  const today = new Date().toLocaleDateString('sv')
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)

  const isMobile = useIsMobile()

  const { createGenericErrorAlert } = useAlert()

  const getTasks = () => {
    return authedAxios
      .get(`/api/task/dashboard?start=${startDate}&end=${endDate}`)
      .then((res) => {
        setTasks(res.data.tasks)
      })
  }

  const getTaskTypes = () => {
    return authedAxios.get('/api/task/types').then((res) => {
      setTaskTypes(res.data.types)
    })
  }

  const init = () => {
    setIsLoading(true)

    Promise.all([getTasks(), getTaskTypes()])
      .then(() => {
        setIsLoading(false)
      })
      .catch(() => {
        setHasError(true)
      })
  }

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (isLoading) return
    getTasks()
  }, [startDate, endDate])

  const updateTaskStatus = (task, e) => {
    const statusTypeId = e.target.value

    const newVal = { ...task, statusTypeId }
    setTasks(tasks.map((t) => (t.id === task.id ? newVal : t)))

    return saveTask(newVal)
  }

  const updateTask = (task, mergeObj) => {
    const newVal = { ...task, ...mergeObj }
    setTasks(tasks.map((t) => (t.id === task.id ? newVal : t)))

    // return saveTask(newVal)
  }

  const saveTask = (task) => {
    const {
      note,
      date,
      customerContactId,
      taskTypeId,
      customerId,
      statusTypeId,
    } = task

    return authedAxios
      .put(`/api/task/${task.id}`, {
        note,
        date,
        customerContactId,
        taskTypeId,
        customerId,
        statusTypeId,
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const updateTaskInList = (task) => {
    setTasks(updateObjectWithMatchingId(tasks, task, true))
  }

  if (hasError) return <GenericLoadError />

  if (isLoading || _taskStatusTypes.loading) return <CircularProgress />

  const headerStyles = {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1,
  }

  return (
    <>
      <AllTaskModals
        currentOpenModal={currentOpenModal}
        currentOpenModalStaging={currentOpenModalStaging}
        setCurrentOpenModal={setCurrentOpenModal}
        setCurrentOpenModalStaging={setCurrentOpenModalStaging}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        updateTaskInList={updateTaskInList}
        currentTask={currentTask}
        setCurrentTask={setCurrentTask}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h6'>Tasks</Typography>
          <Tooltip
            sx={{ ml: 2 }}
            title={
              <Typography sx={{ fontSize: '18px' }}>
                In the task section, you can create tasks to follow up on, that
                are tied to an account.
                <br />
                <br />
                <b>Red:</b> Overdue tasks.
                <br /> <b>Yellow:</b> Tasks with a due date of today
                <br /> <b>White:</b> Tasks with future due date
                <br />
                <b>Green:</b> Completed tasks
              </Typography>
            }
          >
            <Info color='primary'></Info>
          </Tooltip>
        </Box>

        <PastTodayFuture
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />

        {isMobile && (
          <IconButton
            color='primary'
            onClick={() => setCurrentOpenModal('selector')}
          >
            <Add />
          </IconButton>
        )}
        {!isMobile && (
          <Button
            color='primary'
            onClick={() => setCurrentOpenModal('selector')}
            endIcon={<Add />}
            variant='outlined'
          >
            Add Task
          </Button>
        )}
      </Box>
      <TableContainer sx={{ height: 'calc(100% - 48px)' }}>
        <Table>
          <TableHead>
            <TableRow>
              {!isMobile && (
                <TableCell sx={headerStyles}>Created Date</TableCell>
              )}
              <TableCell sx={headerStyles}>Due Date</TableCell>
              <TableCell sx={headerStyles}>Task</TableCell>
              <TableCell sx={headerStyles}>Account</TableCell>
              {/* <TableCell sx={headerStyles}>Description</TableCell> */}
              <TableCell sx={headerStyles}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((t, i) => (
              <TaskItem
                task={t}
                key={i}
                taskTypes={taskTypes}
                taskStatusTypes={taskStatusTypes}
                updateTaskStatus={updateTaskStatus}
                updateTask={updateTask}
                saveTask={saveTask}
                setCurrentOpenModal={setCurrentOpenModal}
                setCurrentTask={setCurrentTask}
              />
            ))}
          </TableBody>
        </Table>
        {tasks.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Typography>
              <i>No Tasks found</i>
            </Typography>
          </Box>
        )}
      </TableContainer>
    </>
  )
}

export default Tasks
